export const AnalyticsView = ({ data }) => {
  if (!data) return <p className="text-base font-medium text-[#D6D6D6]">Loading...</p>;
  const { totals, intents, recent_events, ab_test } = data;
  const abLeader = (ab_test || []).length === 2 && ab_test[0].assigned + ab_test[1].assigned > 0
    ? (ab_test[0].rate === ab_test[1].rate ? null : (ab_test[0].rate > ab_test[1].rate ? "A" : "B"))
    : null;
  const stats = [
    { label: "Sessions", value: totals.sessions },
    { label: "Messages", value: totals.messages },
    { label: "Contacts", value: totals.contacts },
    { label: "Meetings booked", value: totals.bookings },
    { label: "Conversion", value: `${totals.conversion_pct}%` },
  ];
  const maxIntent = Math.max(...intents.map((i) => i.count), 1);

  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="font-display text-3xl text-white">{s.value}</p>
            <p className="text-[15px] font-semibold text-[#D6D6D6] mt-2">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6" data-testid="lead-sources-panel">
        <p className="text-base font-bold text-white mb-1">Lead sources</p>
        <p className="text-[15px] font-medium text-[#D6D6D6] mb-5">Where leads come from, and which channels actually book calls. Tracked from the moment each visitor lands.</p>
        {(data.lead_sources || []).length === 0 ? (
          <p className="text-[15px] font-medium text-[#D6D6D6]">No leads recorded yet.</p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#2E2E2E]">
                <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Channel</th>
                <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Leads</th>
                <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Booked a call</th>
                <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Rate</th>
              </tr>
            </thead>
            <tbody>
              {data.lead_sources.map((r, i) => (
                <tr key={r.channel} data-testid={`lead-source-row-${i}`} className={i % 2 === 1 ? "bg-[#161616]" : ""}>
                  <td className="py-2.5 text-[15px] font-semibold text-white">{r.channel}</td>
                  <td className="py-2.5 text-[15px] font-medium text-[#D6D6D6]">{r.leads}</td>
                  <td className="py-2.5 text-[15px] font-medium text-[#D6D6D6]">{r.booked}</td>
                  <td className={`py-2.5 text-[15px] font-semibold ${r.rate > 0 ? "text-[#4ADE80]" : "text-[#D6D6D6]"}`}>{r.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6" data-testid="audience-segments-panel">
          <p className="text-base font-bold text-white mb-1">Audience interest</p>
          <p className="text-[15px] font-medium text-[#D6D6D6] mb-5">Which audience visitors pick on the homepage selector, and how many of each actually write in.</p>
          {(data.audience_segments || []).length === 0 ? (
            <p className="text-[15px] font-medium text-[#D6D6D6]">No selections recorded yet.</p>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#2E2E2E]">
                  <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Audience</th>
                  <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Selections</th>
                  <th className="pb-2 text-[15px] font-semibold text-[#D6D6D6]">Enquiries</th>
                </tr>
              </thead>
              <tbody>
                {data.audience_segments.map((r, i) => (
                  <tr key={r.segment} data-testid={`audience-segment-row-${i}`} className={i % 2 === 1 ? "bg-[#161616]" : ""}>
                    <td className="py-2.5 text-[15px] font-semibold text-white">{r.segment}</td>
                    <td className="py-2.5 text-[15px] font-medium text-[#D6D6D6]">{r.picks}</td>
                    <td className={`py-2.5 text-[15px] font-semibold ${r.enquiries > 0 ? "text-[#4ADE80]" : "text-[#D6D6D6]"}`}>{r.enquiries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6" data-testid="legal-questions-panel">
          <p className="text-base font-bold text-white mb-1">Legal questions opened</p>
          <p className="text-[15px] font-medium text-[#D6D6D6] mb-5">Which of the eight rulebook questions visitors open most — a read on what worries them.</p>
          {(data.legal_questions || []).length === 0 ? (
            <p className="text-[15px] font-medium text-[#D6D6D6]">No questions opened yet.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto chat-scroll">
              {data.legal_questions.map((r, i) => (
                <div key={r.question} data-testid={`legal-question-row-${i}`}
                  className={`flex items-baseline justify-between gap-4 px-2 py-2 ${i % 2 === 1 ? "bg-[#161616]" : ""}`}>
                  <span className="text-[15px] font-semibold text-white">{r.question}</span>
                  <span className="text-[15px] font-medium text-[#D6D6D6] shrink-0">{r.opens} {r.opens === 1 ? "open" : "opens"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6" data-testid="ab-test-panel">
        <p className="text-base font-bold text-white mb-1">Greeting A/B test</p>
        <p className="text-[15px] font-medium text-[#D6D6D6] mb-5">How each opening line converts to verified contacts.</p>
        {(ab_test || []).length === 0 ? (
          <p className="text-[15px] font-medium text-[#D6D6D6]">No assignments yet. New visitors are split 50/50 once Variant B is set in Agent settings.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ab_test.map((v) => (
              <div key={v.variant} data-testid={`ab-variant-${v.variant}`}
                className={`rounded-xl border p-5 ${abLeader === v.variant ? "border-[#4ADE80]" : "border-[#2E2E2E]"}`}>
                <div className="flex items-baseline justify-between">
                  <p className="font-display text-2xl text-white">Variant {v.variant}</p>
                  {abLeader === v.variant && <span className="text-[15px] font-bold text-[#4ADE80]">Leading</span>}
                </div>
                <p className="font-display text-4xl text-white mt-3">{v.rate}%</p>
                <p className="text-[15px] font-medium text-[#D6D6D6] mt-1">
                  {v.converted} verified / {v.assigned} visitors
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6">
          <p className="text-base font-bold text-white mb-5">What people come in for</p>
          <div className="space-y-3">
            {intents.map((i) => (
              <div key={i.intent}>
                <div className="flex justify-between text-[15px] font-medium mb-1">
                  <span className="text-[#D6D6D6]">{i.intent}</span>
                  <span className="text-[#D6D6D6]">{i.count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#2E2E2E]">
                  <div className="h-full rounded-full bg-white" style={{ width: `${(i.count / maxIntent) * 100}%` }} />
                </div>
              </div>
            ))}
            {intents.length === 0 && <p className="text-[15px] font-medium text-[#D6D6D6]">No data yet</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6">
          <p className="text-base font-bold text-white mb-5">Recent activity</p>
          <div className="space-y-2 max-h-72 overflow-y-auto chat-scroll">
            {(recent_events || []).map((e) => (
              <div key={e.id} className="flex items-center gap-3 text-[15px] font-medium border-b border-[#2E2E2E] pb-2">
                <span className="text-[#D6D6D6] shrink-0">{new Date(e.created_at).toLocaleTimeString()}</span>
                <span className="text-[#D6D6D6]">{e.type.replace(/_/g, " ")}</span>
                {e.mocked && <span className="text-[#FF5C5C] text-[14px] font-bold border border-[#FF5C5C] rounded-full px-2 py-0.5">Mocked</span>}
                {e.channel && <span className="text-[#D6D6D6]">{e.channel}</span>}
              </div>
            ))}
            {(!recent_events || recent_events.length === 0) && <p className="text-[15px] font-medium text-[#D6D6D6]">No events yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
